import { supabase } from "@/integrations/supabase/client";
import { OrderStatus } from "@/types/tms";
import { OrderWithRelations } from "@/components/schedule/types";
import { resolveLegIdentity } from "@/lib/leg-progression";
import { useLegStatusProgression } from "@/hooks/useLegStatusProgression";
import { ORDER_MUTATION_QUERY_KEYS } from "@/lib/order-cache-keys";
import { useTenant } from "@/hooks/useTenant";
import { useEapiAuth } from "@/hooks/useEapiAuth";
import { useToast } from "@/hooks/use-toast";
import { fireAymsEventForTrailer } from "@/lib/ayms-event";
import { notifyOrderStatus } from "@/lib/notify-order-status";
import { syncTrailerBol } from "@/lib/trailer-bol-sync";
// Shared with the order-level settlement path (src/lib/trailer-settle.ts) so
// the driver app and dispatcher closes resolve destinations identically.
import { resolveYardLocation } from "@/lib/trailer-settle";
import { buildRelayDecision } from "@/lib/vas-relay";

/**
 * Driver-side leg/order status promotion.
 *
 * The status write itself lives in the shared `useLegStatusProgression` hook
 * (which calls `progressLeg`) so dispatch's Update Stop Status modal and this
 * driver flow can never diverge on stop/parent semantics. Everything below is
 * the DRIVER-ONLY side-effect set — trailer events, AYMS, VAS relay and the
 * customer email — passed in as `onAfterProgress`. Dispatch deliberately fires
 * none of it.
 */
async function resolveLegTractorName(
  legId: string | null,
  orderTractorName: string | null | undefined,
): Promise<string | null | undefined> {
  if (!legId) return orderTractorName;
  const { data } = await supabase
    .from("legs")
    .select("tractors(name)")
    .eq("id", legId)
    .maybeSingle();
  return (data as { tractors?: { name?: string | null } | null } | null)?.tractors?.name;
}

export function useDriverLegStatusUpdate(order: OrderWithRelations, driverName?: string) {
  const { toast } = useToast();
  const { tenantId, eapiEnv, activeTenant } = useTenant();
  const { eapiClient } = useEapiAuth();

  const { progress, isUpdating } = useLegStatusProgression({
    actorLabel: driverName || "Driver",
    requirePriorInTransit: true,
    invalidateKeys: [
      ...ORDER_MUTATION_QUERY_KEYS,
      ["driver-current-assignment"],
      ["driver-current-assignments"],
      ["driver-next-assignment"],
      ["driver-upcoming-assignments"],
      ["driver-recently-completed"],
      ["driver-assignment-detail"],
      ["trailers"],
    ],
    describeSuccess: ({ newStatus, result }) =>
      result.isMultiLeg && !result.parentPromoted
        ? `Leg marked as ${newStatus === "in_transit" ? "In Transit" : "Delivered"} (waiting on other legs)`
        : `Order marked as ${newStatus === "in_transit" ? "In Transit" : "Delivered"}`,
    onAfterProgress: async ({ parentOrderId, legId, newStatus, result }) => {
      // Trailer events: caller-owned side effect. Driver flow fires only on
      // the boundary leg (first leg's "Ready to Depart" / last leg's
      // "I've Arrived"). Single-leg orders are both first and last.
      const shouldFireTrailerEvent = !!order.trailer_id && result.isBoundary;

      if (shouldFireTrailerEvent) {
        let trailerLookup = supabase
          .from("trailers")
          .select("id")
          .eq("trailer_id", order.trailer_id);
        if (tenantId) trailerLookup = trailerLookup.eq("tenant_id", tenantId);
        const { data: trailer } = await trailerLookup.maybeSingle();

        if (trailer) {
          // The gate tractor is this leg's own tractor. `orders.assigned_tractor`
          // only mirrors leg 1, so on a multi-leg order the final check-in
          // would report the wrong truck. Only legacy orders with no leg row
          // read the order-level value.
          const tractorId = await resolveLegTractorName(legId, order.assigned_tractor?.name);

          if (newStatus === "in_transit") {
            const { locationId: fromLocationId, trackInYms: fromYms } =
              await resolveYardLocation(
                (order as any).ship_from_location_id ?? null,
                order.ship_from_street_address ?? null,
              );

            // Always reflect "left yard" on the trailer regardless of origin
            // tracking. Skip the YMS-specific side effects (trailer_event +
            // AYMS event) when origin isn't a YMS-tracked yard.
            if (fromYms && fromLocationId) {
              await supabase.from("trailer_events").insert({
                trailer_id: trailer.id,
                event_type: "checked_out" as const,
                location_id: fromLocationId,
                created_by_user_id: (await supabase.auth.getUser()).data.user?.id,
                // Without an explicit tenant, the column default (Dev) files
                // Production events under the wrong tenant.
                ...(tenantId ? { tenant_id: tenantId } : {}),
              });

              void fireAymsEventForTrailer({
                trailerHumanId: order.trailer_id!,
                direction: "out",
                tenantId,
                eapiClient,
                locationId: fromLocationId,
                tractorId,
              });
            } else {
              console.warn(
                "[useDriverLegStatusUpdate] checkout: origin not YMS-tracked or unresolved; skipping trailer_event + AYMS",
                { fromLocationId, fromYms, fkId: (order as any).ship_from_location_id, address: order.ship_from_street_address },
              );
            }

            await supabase.from("trailers").update({
              current_location_status: "in_transit" as const,
              current_location_id: null,
            }).eq("id", trailer.id);
          } else if (newStatus === "delivered") {
            const { locationId: toLocationId, trackInYms: toYms } =
              await resolveYardLocation(
                (order as any).ship_to_location_id ?? null,
                order.ship_to_street_address ?? null,
              );

            if (toYms && toLocationId) {
              const { data: newEvt } = await supabase
                .from("trailer_events")
                .insert({
                  trailer_id: trailer.id,
                  event_type: "checked_in" as const,
                  location_id: toLocationId,
                  created_by_user_id: (await supabase.auth.getUser()).data.user?.id,
                  ...(tenantId ? { tenant_id: tenantId } : {}),
                })
                .select("id")
                .single();

              await supabase.from("trailers").update({
                current_location_status: "at_site" as const,
                current_location_id: toLocationId,
              }).eq("id", trailer.id);

              void fireAymsEventForTrailer({
                trailerHumanId: order.trailer_id!,
                direction: "in",
                tenantId,
                eapiClient,
                locationId: toLocationId,
                tractorId,
              });

              // Reverse-direction sync: the driver just created a fresh
              // `checked_in` event. If the trailer already carries a BoL
              // from a prior event, carry it forward onto this event and
              // fan it out to any active orders assigned to this trailer
              // (including this order — sync-trailer-bol-to-order is
              // idempotent). Best-effort, never blocks the driver flow.
              if (newEvt?.id) {
                void syncTrailerBol({
                  direction: "trailer-gains-bol",
                  trailerHumanId: order.trailer_id!,
                  tenantId,
                  tenantSlug: activeTenant,
                  carryForward: {
                    trailerDbId: trailer.id,
                    newEventId: newEvt.id,
                  },
                });
              }
            } else {
              // Non-YMS destination (or unresolved): we have no operational
              // visibility into where the trailer actually is, so mark it
              // `unknown` rather than fabricating an `at_site` row with a
              // null/garbage location_id. This is the structural fix that
              // prevents the historical "at_site + null location" drift.
              console.warn(
                "[useDriverLegStatusUpdate] delivery: destination not YMS-tracked or unresolved; marking trailer unknown",
                { toLocationId, toYms, fkId: (order as any).ship_to_location_id, address: order.ship_to_street_address },
              );

              await supabase.from("trailers").update({
                current_location_status: "unknown" as const,
                current_location_id: null,
              }).eq("id", trailer.id);

              toast({
                title: "Trailer status set to Unknown",
                description:
                  "Delivered to a non-YMS location. Check the trailer in manually from Yard if needed.",
              });
            }
          }
        }
      }

      // VAS stage transition: mirror EVERY leg transition (in_transit /
      // delivered) into the customer's VAS system via EAPI. Best-effort,
      // never blocks the driver flow. Gating goes through the SAME shared
      // decision helper the asp-trips callback uses (`@/lib/vas-relay`):
      // explicit `vas_enabled` opt-in + a numeric VAS tender id. An org
      // mapping alone is not a VAS signal — that table also backs TaaS
      // inbound tenant resolution for non-VAS customers, which is how
      // non-VAS traffic used to reach /v1/vas/stage-transition.
      // Do NOT gate on `result.parentPromoted`: for
      // multi-leg (relay) orders, interim legs never promote the parent,
      // and gating on it silently drops every intermediate mission event
      // so EAPI/VAS sees nothing until the final leg delivers.
      if (
        (newStatus === "in_transit" || newStatus === "delivered") &&
        order.trip_type === "Manual" &&
        order.tender_id &&
        order.billing_party_id
      ) {
        const currentStage =
          newStatus === "in_transit" ? "TRIP_STAGE_ENROUTE" : "TRIP_STAGE_COMPLETED";
        (async () => {
          try {
            let orgLookup = supabase
              .from("customer_tenant_org_ids")
              .select("onramp_org_id, vas_enabled")
              .eq("customer_id", order.billing_party_id!);
            if (tenantId) orgLookup = orgLookup.eq("tenant_id", tenantId);
            const { data: orgRow } = await orgLookup.maybeSingle();
            const org = orgRow as
              | { onramp_org_id?: string | null; vas_enabled?: boolean | null }
              | null;

            // Multi-leg VAS: include the transitioning leg's customer_leg_id
            // so EAPI emits a mission-scoped event instead of trip-scoped.
            // Omitted when null so single-leg / non-VAS callers stay byte-identical.
            const { data: legRow } = await supabase
              .from("legs")
              .select("customer_leg_id")
              .eq("id", legId)
              .maybeSingle();
            const customerLegId =
              (legRow as { customer_leg_id?: string | null } | null)?.customer_leg_id ?? null;

            const decision = buildRelayDecision({
              tenderId: order.tender_id ?? null,
              orgId: org?.onramp_org_id ?? null,
              currentStage,
              legCustomerLegId: customerLegId,
              vasEnabled: !!org?.vas_enabled,
            });
            if (decision.relay !== true) {
              console.info(
                "[useDriverLegStatusUpdate] vas stage-transition skipped:",
                decision.reason,
              );
              return;
            }

            const { error: invokeError } = await supabase.functions.invoke("eapi-proxy", {
              body: {
                method: "POST",
                path: "/v1/vas/stage-transition",
                environment: eapiEnv,
                body: decision.payload,
              },
            });
            if (invokeError) {
              console.warn("[useDriverLegStatusUpdate] vas stage-transition failed:", invokeError);
            }
          } catch (err) {
            console.warn("[useDriverLegStatusUpdate] vas stage-transition threw:", err);
          }
        })();
      }

      // Best-effort customer email notification. send-email enforces all
      // eligibility (non-AURY, has email, parent-only, de-dup) itself.
      if (result.parentPromoted && (newStatus === "in_transit" || newStatus === "delivered")) {
        void notifyOrderStatus({
          orderId: parentOrderId,
          newStatus,
          initiator: "driver-app",
          tenantSlug: activeTenant,
        });
      }
    },
  });

  const updateOrderStatus = async (newStatus: OrderStatus) => {
    const { parentOrderId, legId } = resolveLegIdentity(order as any);
    await progress({ parentOrderId, legId, newStatus });
  };

  return { updateOrderStatus, isUpdating };
}
